import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Discrete vs Continuous: PMF and PDF side by side
// ─────────────────────────────────────────────────────────────────
function DiagramPMFvsPDF() {
  const W = 900, H = 320;

  // Left: Binomial PMF bars  n=10, p=0.4
  const n = 10, p = 0.4;
  const factorial = (k) => { let f=1; for(let i=2;i<=k;i++) f*=i; return f; };
  const binom = (k) => (factorial(n)/(factorial(k)*factorial(n-k))) * Math.pow(p,k) * Math.pow(1-p,n-k);
  const pmfVals = Array.from({length:n+1},(_,k) => ({ k, prob: binom(k) }));

  const lPanX=20, lPanW=400, lPanH=H-20;
  const barAreaX=lPanX+50, barAreaY=35, barAreaW=lPanW-70, barAreaH=210;
  const maxProb = Math.max(...pmfVals.map(d=>d.prob));
  const barW = barAreaW / (n+1) - 3;

  // Right: Normal PDF curve
  const rPanX=490, rPanW=400, rPanH=H-20;
  const curveAreaX=rPanX+50, curveAreaY=35, curveAreaW=rPanW-70, curveAreaH=210;
  const mu=0, sigma=1;
  const xMin=-3.5, xMax=3.5;
  const normpdf = (x) => (1/(sigma*Math.sqrt(2*Math.PI)))*Math.exp(-0.5*((x-mu)/sigma)**2);
  const pts = 120;
  const curvePoints = Array.from({length:pts},(_,i)=>{
    const x = xMin + (xMax-xMin)*i/(pts-1);
    const y = normpdf(x);
    return { x, y };
  });
  const maxY = normpdf(mu);
  const toSvgX = (x) => curveAreaX + (x-xMin)/(xMax-xMin)*curveAreaW;
  const toSvgY = (y) => curveAreaY + curveAreaH - (y/maxY)*curveAreaH;

  // Build polyline string
  const polyPts = curvePoints.map(({x,y})=>`${toSvgX(x).toFixed(1)},${toSvgY(y).toFixed(1)}`).join(' ');

  // Shade area under -1 to +1 (±1σ region)
  const shadeX1=-1, shadeX2=1;
  const shadePts = curvePoints.filter(p=>p.x>=shadeX1&&p.x<=shadeX2);
  const shadePolyPts = [
    `${toSvgX(shadeX1).toFixed(1)},${(curveAreaY+curveAreaH).toFixed(1)}`,
    ...shadePts.map(({x,y})=>`${toSvgX(x).toFixed(1)},${toSvgY(y).toFixed(1)}`),
    `${toSvgX(shadeX2).toFixed(1)},${(curveAreaY+curveAreaH).toFixed(1)}`,
  ].join(' ');

  const xAxisTicks = [-3,-2,-1,0,1,2,3];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Discrete vs Continuous Distributions — PMF (Binomial) and PDF (Normal)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT PANEL: Binomial PMF */}
        <rect x={lPanX} y={10} width={lPanW} height={lPanH} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={lPanX+4} y={14} width={lPanW-8} height={20} rx={3} fill="#2E75B6"/>
        <text x={lPanX+lPanW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Discrete: Binomial PMF  n=10, p=0.4
        </text>

        {/* Bars */}
        {pmfVals.map(({k, prob})=>{
          const bx = barAreaX + k*(barAreaW/(n+1));
          const bh = (prob/maxProb)*barAreaH;
          const by = barAreaY + barAreaH - bh;
          const isMode = Math.abs(prob-maxProb)<0.0001;
          return (
            <g key={k}>
              <rect x={bx+1} y={by} width={barW} height={bh} rx={2}
                fill={isMode?'#c0392b':'#2E75B6'} opacity="0.82"/>
              <text x={bx+barW/2+1} y={by-3} textAnchor="middle" fontSize="9" fill="#444">
                {prob>0.01?prob.toFixed(3):''}
              </text>
              <text x={bx+barW/2+1} y={barAreaY+barAreaH+13} textAnchor="middle" fontSize="10" fill="#666">{k}</text>
            </g>
          );
        })}

        {/* Y axis ticks */}
        {[0,0.1,0.2,0.3].map((v,i)=>(
          <g key={i}>
            <line x1={barAreaX-2} y1={barAreaY+barAreaH-(v/maxProb)*barAreaH}
              x2={barAreaX+barAreaW} y2={barAreaY+barAreaH-(v/maxProb)*barAreaH}
              stroke="#eee" strokeWidth="1"/>
            <text x={barAreaX-5} y={barAreaY+barAreaH-(v/maxProb)*barAreaH+4}
              textAnchor="end" fontSize="9" fill="#888">{v.toFixed(1)}</text>
          </g>
        ))}

        {/* X axis */}
        <line x1={barAreaX} y1={barAreaY+barAreaH} x2={barAreaX+barAreaW} y2={barAreaY+barAreaH}
          stroke="#aaa" strokeWidth="1.5"/>
        <text x={barAreaX+barAreaW/2} y={barAreaY+barAreaH+28} textAnchor="middle" fontSize="11" fill="#555">
          k (number of successes)
        </text>
        <text x={barAreaX-35} y={barAreaY+barAreaH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${barAreaX-35},${barAreaY+barAreaH/2})`}>P(X=k)</text>

        {/* Key facts */}
        <text x={lPanX+lPanW/2} y={barAreaY+barAreaH+50} textAnchor="middle" fontSize="10" fill="#2E75B6" fontWeight="700">
          E[X] = np = 4.0   |   Var(X) = np(1-p) = 2.4   |   Mode = 4 (red)
        </text>
        <text x={lPanX+lPanW/2} y={barAreaY+barAreaH+64} textAnchor="middle" fontSize="10" fill="#555">
          P(X=k) = C(n,k)·pᵏ·(1-p)ⁿ⁻ᵏ   |   ΣP(X=k)=1
        </text>

        {/* RIGHT PANEL: Normal PDF */}
        <rect x={rPanX} y={10} width={rPanW} height={rPanH} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={rPanX+4} y={14} width={rPanW-8} height={20} rx={3} fill="#27ae60"/>
        <text x={rPanX+rPanW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Continuous: Normal PDF  μ=0, σ=1
        </text>

        {/* Grid lines */}
        {[0,0.1,0.2,0.3,0.4].map((v,i)=>(
          <g key={i}>
            <line x1={curveAreaX} y1={toSvgY(v)} x2={curveAreaX+curveAreaW} y2={toSvgY(v)}
              stroke="#eee" strokeWidth="1"/>
            <text x={curveAreaX-5} y={toSvgY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v.toFixed(1)}</text>
          </g>
        ))}

        {/* Shaded ±1σ area */}
        <polygon points={shadePolyPts} fill="#2E75B644"/>

        {/* Curve */}
        <polyline points={polyPts} fill="none" stroke="#27ae60" strokeWidth="2.5"/>

        {/* Baseline */}
        <line x1={curveAreaX} y1={curveAreaY+curveAreaH} x2={curveAreaX+curveAreaW} y2={curveAreaY+curveAreaH}
          stroke="#aaa" strokeWidth="1.5"/>

        {/* X axis ticks */}
        {xAxisTicks.map(v=>(
          <g key={v}>
            <line x1={toSvgX(v)} y1={curveAreaY+curveAreaH} x2={toSvgX(v)} y2={curveAreaY+curveAreaH+4}
              stroke="#aaa" strokeWidth="1"/>
            <text x={toSvgX(v)} y={curveAreaY+curveAreaH+14} textAnchor="middle" fontSize="10" fill="#666">{v}</text>
          </g>
        ))}

        {/* σ markers */}
        {[-1,1].map(s=>(
          <g key={s}>
            <line x1={toSvgX(s)} y1={curveAreaY+5} x2={toSvgX(s)} y2={curveAreaY+curveAreaH}
              stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="5,3"/>
            <text x={toSvgX(s)} y={curveAreaY+3} textAnchor="middle" fontSize="9" fill="#2E75B6">{s>0?'+σ':'-σ'}</text>
          </g>
        ))}
        <text x={toSvgX(0)} y={curveAreaY+curveAreaH/2} textAnchor="middle" fontSize="10" fill="#2E75B6" fontWeight="700">68%</text>

        <text x={rPanX+rPanW/2} y={curveAreaY+curveAreaH+28} textAnchor="middle" fontSize="11" fill="#555">
          z  (standard deviations from mean)
        </text>
        <text x={curveAreaX-35} y={curveAreaY+curveAreaH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${curveAreaX-35},${curveAreaY+curveAreaH/2})`}>f(x)</text>

        <text x={rPanX+rPanW/2} y={curveAreaY+curveAreaH+50} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">
          E[X] = 0   |   Var(X) = 1   |   Shaded = P(-1≤X≤1) ≈ 68.3%
        </text>
        <text x={rPanX+rPanW/2} y={curveAreaY+curveAreaH+64} textAnchor="middle" fontSize="10" fill="#555">
          f(x) = (1/√2π)·exp(-x²/2)   |   ∫f(x)dx = 1 over all x
        </text>
      </svg>
      <div style={S.dCaption}>
        A discrete distribution assigns probability to individual integer values via a PMF: P(X=k) ≥ 0 and the sum over all k equals 1. A continuous distribution uses a PDF where the area under the curve equals 1 and probability of any single point is zero — only intervals have positive probability. The Binomial and Normal are the most fundamental examples of each type.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Common Discrete Distributions
// ─────────────────────────────────────────────────────────────────
function DiagramDiscreteDistributions() {
  const W = 900, H = 340;

  // Three distributions: Bernoulli, Geometric, Poisson(λ=3)
  const panels = [
    {
      title: 'Bernoulli(p=0.35)',
      color: '#2E75B6', fill: '#EBF3FB',
      desc: 'Single trial, 2 outcomes',
      vals: [{ k:0, p:0.65 }, { k:1, p:0.35 }],
      maxK: 1, formula: 'P(X=k) = p^k(1-p)^(1-k)',
      stats: 'E=0.35  Var=0.228',
    },
    {
      title: 'Geometric(p=0.4)',
      color: '#c0392b', fill: '#fff0f0',
      desc: 'Trials until first success',
      vals: Array.from({length:8},(_,k)=>({ k:k+1, p: 0.4*Math.pow(0.6,k) })),
      maxK: 8, formula: 'P(X=k) = (1-p)^(k-1)·p',
      stats: 'E=1/p=2.5  Var=6.25',
    },
    {
      title: 'Poisson(λ=3)',
      color: '#9b59b6', fill: '#f5d0ff',
      desc: 'Events in fixed interval',
      vals: Array.from({length:10},(_,k)=>({
        k, p: Math.exp(-3)*Math.pow(3,k)/[1,1,2,6,24,120,720,5040,40320,362880][k]
      })),
      maxK: 9, formula: 'P(X=k) = e^(-λ)·λᵏ/k!',
      stats: 'E=λ=3  Var=λ=3',
    },
  ];

  const panW = (W - 28) / 3;
  const barAreaH = 160, barAreaY = 56;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Common Discrete Distributions — Bernoulli, Geometric, Poisson</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {panels.map(({ title, color, fill, desc, vals, maxK, formula, stats }, pi) => {
          const px = 10 + pi*(panW+4);
          const barAreaX = px + 44;
          const barAreaW = panW - 58;
          const maxP = Math.max(...vals.map(v=>v.p));
          const bw = barAreaW / (maxK + 1) - 2;

          return (
            <g key={pi}>
              <rect x={px} y={10} width={panW} height={H-16} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={38} rx={3} fill={color}/>
              <text x={px+panW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={44} textAnchor="middle" fontSize="10" fill="#ffffffcc">{desc}</text>

              {/* Y axis */}
              <line x1={barAreaX} y1={barAreaY} x2={barAreaX} y2={barAreaY+barAreaH} stroke="#bbb" strokeWidth="1"/>
              {/* X axis */}
              <line x1={barAreaX} y1={barAreaY+barAreaH} x2={barAreaX+barAreaW} y2={barAreaY+barAreaH} stroke="#bbb" strokeWidth="1"/>

              {/* Gridlines */}
              {[0.1,0.2,0.3].map(v => (
                v <= maxP + 0.05 ? (
                  <g key={v}>
                    <line x1={barAreaX} y1={barAreaY+barAreaH-(v/maxP)*barAreaH}
                      x2={barAreaX+barAreaW} y2={barAreaY+barAreaH-(v/maxP)*barAreaH}
                      stroke="#ddd" strokeWidth="0.8"/>
                    <text x={barAreaX-3} y={barAreaY+barAreaH-(v/maxP)*barAreaH+4}
                      textAnchor="end" fontSize="8" fill="#999">{v.toFixed(1)}</text>
                  </g>
                ) : null
              ))}

              {vals.map(({ k, p }) => {
                const bx = barAreaX + k*(barAreaW/(maxK+1));
                const bh = (p/maxP)*barAreaH;
                const by = barAreaY + barAreaH - bh;
                return (
                  <g key={k}>
                    <rect x={bx+1} y={by} width={bw} height={bh} rx={2} fill={color} opacity="0.8"/>
                    <text x={bx+bw/2+1} y={barAreaY+barAreaH+12} textAnchor="middle" fontSize="9" fill="#555">{k}</text>
                  </g>
                );
              })}

              {/* Formula box */}
              <rect x={px+6} y={barAreaY+barAreaH+20} width={panW-12} height={20} rx={3}
                fill="#fff" stroke={color} strokeWidth="1"/>
              <text x={px+panW/2} y={barAreaY+barAreaH+33} textAnchor="middle" fontSize="9.5"
                fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{formula}</text>

              {/* Stats */}
              <text x={px+panW/2} y={barAreaY+barAreaH+56} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">{stats}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The Bernoulli models a single binary trial; the Geometric counts how many trials until the first success (memoryless property: future trials are unaffected by past failures); the Poisson models the number of rare events in a fixed interval given a known average rate λ. All three sum their PMF to exactly 1 across all non-negative integers.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Common Continuous Distributions
// ─────────────────────────────────────────────────────────────────
function DiagramContinuousDistributions() {
  const W = 900, H = 320;

  // Three panels: Uniform[0,1], Exponential(λ=1), Normal variants
  const panW = (W - 28) / 3;
  const cAreaH = 180, cAreaY = 50;

  // Uniform
  const uColor='#e07b00', uFill='#fff7ee';
  const uLeft=0, uRight=1;

  // Exponential lambda=1
  const eColor='#27ae60', eFill='#f0fff4';
  const lambda=1;
  const expPts = Array.from({length:80},(_,i)=>{ const x=i*0.06; return { x, y: lambda*Math.exp(-lambda*x) }; });

  // Normal family (μ=0, σ=0.5,1,2)
  const nColor='#2E75B6', nFill='#EBF3FB';
  const normals = [
    { sigma:0.5, color:'#c0392b', lbl:'σ=0.5' },
    { sigma:1,   color:'#2E75B6', lbl:'σ=1' },
    { sigma:2,   color:'#27ae60', lbl:'σ=2' },
  ];
  const nXMin=-4, nXMax=4;
  const npdf = (x,sig) => (1/(sig*Math.sqrt(2*Math.PI)))*Math.exp(-0.5*(x/sig)**2);
  const nMaxY = npdf(0, 0.5);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Common Continuous Distributions — Uniform, Exponential, Normal Family</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── Panel 1: Uniform ── */}
        {(() => {
          const px = 10;
          const ax = px+46, aw = panW-60, ah=cAreaH, ay=cAreaY;
          const fx = (x) => ax + x*aw;
          const fy = (y) => ay + ah - y*ah;
          return (
            <g>
              <rect x={px} y={10} width={panW} height={H-16} rx={5} fill={uFill} stroke={uColor} strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={32} rx={3} fill={uColor}/>
              <text x={px+panW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Uniform Distribution</text>
              <text x={px+panW/2} y={42} textAnchor="middle" fontSize="10" fill="#ffffffcc">U(a=0, b=1)  — constant density</text>

              {/* Grid */}
              {[0.5,1.0].map(v => (
                <g key={v}>
                  <line x1={ax} y1={fy(v)} x2={ax+aw} y2={fy(v)} stroke="#ddd" strokeWidth="0.8"/>
                  <text x={ax-4} y={fy(v)+4} textAnchor="end" fontSize="9" fill="#888">{v.toFixed(1)}</text>
                </g>
              ))}
              {/* Axes */}
              <line x1={ax} y1={ay} x2={ax} y2={ay+ah} stroke="#bbb" strokeWidth="1"/>
              <line x1={ax} y1={ay+ah} x2={ax+aw} y2={ay+ah} stroke="#bbb" strokeWidth="1"/>

              {/* Uniform rectangle */}
              <rect x={fx(0)} y={fy(1)} width={aw} height={ah} fill={`${uColor}33`} stroke={uColor} strokeWidth="2"/>

              {/* x ticks */}
              {[0,0.25,0.5,0.75,1].map(v=>(
                <g key={v}>
                  <line x1={fx(v)} y1={ay+ah} x2={fx(v)} y2={ay+ah+4} stroke="#aaa" strokeWidth="1"/>
                  <text x={fx(v)} y={ay+ah+14} textAnchor="middle" fontSize="9" fill="#666">{v}</text>
                </g>
              ))}
              <text x={ax+aw/2} y={ay+ah+27} textAnchor="middle" fontSize="10" fill="#555">x</text>
              <text x={ax-32} y={ay+ah/2} textAnchor="middle" fontSize="10" fill="#555"
                transform={`rotate(-90,${ax-32},${ay+ah/2})`}>f(x)</text>

              <rect x={px+6} y={ay+ah+35} width={panW-12} height={20} rx={3} fill="#fff" stroke={uColor} strokeWidth="1"/>
              <text x={px+panW/2} y={ay+ah+48} textAnchor="middle" fontSize="9.5"
                fill={uColor} fontFamily="'Courier New',monospace" fontWeight="700">f(x) = 1/(b-a) = 1  for x∈[0,1]</text>
              <text x={px+panW/2} y={ay+ah+70} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">
                E=0.5   Var=1/12≈0.083
              </text>
            </g>
          );
        })()}

        {/* ── Panel 2: Exponential ── */}
        {(() => {
          const px = 10 + panW + 4;
          const ax = px+46, aw = panW-60, ah=cAreaH, ay=cAreaY;
          const xMax = 4.7;
          const toX = (x) => ax + (x/xMax)*aw;
          const toY = (y) => ay + ah - (y/1)*ah;
          const expPolyPts = expPts.filter(p=>p.x<=xMax).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`).join(' ');
          // Shade 0 to 1 (mean)
          const shadePts2 = expPts.filter(p=>p.x<=1).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`);
          const shadeArea2 = [`${toX(0)},${ay+ah}`, ...shadePts2, `${toX(1)},${ay+ah}`].join(' ');

          return (
            <g>
              <rect x={px} y={10} width={panW} height={H-16} rx={5} fill={eFill} stroke={eColor} strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={32} rx={3} fill={eColor}/>
              <text x={px+panW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Exponential Distribution</text>
              <text x={px+panW/2} y={42} textAnchor="middle" fontSize="10" fill="#ffffffcc">Exp(λ=1)  — memoryless waiting times</text>

              {/* Grid */}
              {[0.2,0.4,0.6,0.8,1.0].map(v=>(
                <g key={v}>
                  <line x1={ax} y1={toY(v)} x2={ax+aw} y2={toY(v)} stroke="#ddd" strokeWidth="0.8"/>
                  <text x={ax-4} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v.toFixed(1)}</text>
                </g>
              ))}
              <line x1={ax} y1={ay} x2={ax} y2={ay+ah} stroke="#bbb" strokeWidth="1"/>
              <line x1={ax} y1={ay+ah} x2={ax+aw} y2={ay+ah} stroke="#bbb" strokeWidth="1"/>

              {/* Shaded mean region */}
              <polygon points={shadeArea2} fill={`${eColor}44`}/>
              {/* Curve */}
              <polyline points={expPolyPts} fill="none" stroke={eColor} strokeWidth="2.5"/>
              {/* Mean marker */}
              <line x1={toX(1)} y1={ay+10} x2={toX(1)} y2={ay+ah} stroke={eColor} strokeWidth="1.5" strokeDasharray="5,3"/>
              <text x={toX(1)+3} y={ay+20} fontSize="9" fill={eColor} fontWeight="700">μ=1/λ=1</text>

              {[0,1,2,3,4].map(v=>(
                <g key={v}>
                  <line x1={toX(v)} y1={ay+ah} x2={toX(v)} y2={ay+ah+4} stroke="#aaa" strokeWidth="1"/>
                  <text x={toX(v)} y={ay+ah+14} textAnchor="middle" fontSize="9" fill="#666">{v}</text>
                </g>
              ))}
              <text x={ax+aw/2} y={ay+ah+27} textAnchor="middle" fontSize="10" fill="#555">x</text>
              <text x={ax-32} y={ay+ah/2} textAnchor="middle" fontSize="10" fill="#555"
                transform={`rotate(-90,${ax-32},${ay+ah/2})`}>f(x)</text>

              <rect x={px+6} y={ay+ah+35} width={panW-12} height={20} rx={3} fill="#fff" stroke={eColor} strokeWidth="1"/>
              <text x={px+panW/2} y={ay+ah+48} textAnchor="middle" fontSize="9.5"
                fill={eColor} fontFamily="'Courier New',monospace" fontWeight="700">f(x) = λe^(-λx)  for x≥0</text>
              <text x={px+panW/2} y={ay+ah+70} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">
                E=1/λ=1   Var=1/λ²=1
              </text>
            </g>
          );
        })()}

        {/* ── Panel 3: Normal family ── */}
        {(() => {
          const px = 10 + 2*(panW+4);
          const ax = px+46, aw = panW-60, ah=cAreaH, ay=cAreaY;
          const toNX = (x) => ax + (x-nXMin)/(nXMax-nXMin)*aw;
          const toNY = (y) => ay + ah - (y/nMaxY)*ah;

          return (
            <g>
              <rect x={px} y={10} width={panW} height={H-16} rx={5} fill={nFill} stroke={nColor} strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={32} rx={3} fill={nColor}/>
              <text x={px+panW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Normal Distribution Family</text>
              <text x={px+panW/2} y={42} textAnchor="middle" fontSize="10" fill="#ffffffcc">N(μ=0, σ)  — effect of varying σ</text>

              {/* Grid */}
              {[0.2,0.4,0.6,0.8].map(v=>(
                v<=nMaxY ? (
                  <g key={v}>
                    <line x1={ax} y1={toNY(v)} x2={ax+aw} y2={toNY(v)} stroke="#ddd" strokeWidth="0.8"/>
                    <text x={ax-4} y={toNY(v)+4} textAnchor="end" fontSize="8" fill="#888">{v.toFixed(1)}</text>
                  </g>
                ) : null
              ))}
              <line x1={ax} y1={ay} x2={ax} y2={ay+ah} stroke="#bbb" strokeWidth="1"/>
              <line x1={ax} y1={ay+ah} x2={ax+aw} y2={ay+ah} stroke="#bbb" strokeWidth="1"/>

              {/* Normal curves */}
              {normals.map(({ sigma, color, lbl }) => {
                const curvePts = Array.from({length:100},(_,i)=>{
                  const x = nXMin + (nXMax-nXMin)*i/99;
                  return `${toNX(x).toFixed(1)},${toNY(npdf(x,sigma)).toFixed(1)}`;
                }).join(' ');
                return <polyline key={sigma} points={curvePts} fill="none" stroke={color} strokeWidth="2.2"/>;
              })}

              {/* x ticks */}
              {[-3,-2,-1,0,1,2,3].map(v=>(
                <g key={v}>
                  <line x1={toNX(v)} y1={ay+ah} x2={toNX(v)} y2={ay+ah+4} stroke="#aaa" strokeWidth="1"/>
                  <text x={toNX(v)} y={ay+ah+14} textAnchor="middle" fontSize="9" fill="#666">{v}</text>
                </g>
              ))}
              <text x={ax+aw/2} y={ay+ah+27} textAnchor="middle" fontSize="10" fill="#555">x</text>

              {/* Legend */}
              {normals.map(({sigma,color,lbl},i)=>(
                <g key={sigma}>
                  <line x1={ax+4} y1={ay+ah+42+i*16} x2={ax+18} y2={ay+ah+42+i*16} stroke={color} strokeWidth="2"/>
                  <text x={ax+22} y={ay+ah+46+i*16} fontSize="10" fill={color} fontWeight="700">{lbl}</text>
                </g>
              ))}

              <text x={px+panW/2} y={ay+ah+100} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">
                E=μ=0   Var=σ²   Symmetric
              </text>
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>
        The Uniform distribution assigns equal density everywhere in [a,b]. The Exponential distribution is the continuous analogue of the Geometric — it models waiting times between Poisson events and has the memoryless property P(X{'>'}s+t|X{'>'}s)=P(X{'>'}t). The Normal family shows how σ controls spread while keeping area=1; larger σ flattens and widens the bell curve symmetrically around μ.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — CDF: Cumulative Distribution Function
// ─────────────────────────────────────────────────────────────────
function DiagramCDF() {
  const W = 900, H = 330;
  const panW = (W-28)/2;
  const aH=200, aY=48;

  // Discrete CDF: Binomial n=5, p=0.5
  const n=5, p=0.5;
  const fact = k => k<=1?1:k*fact(k-1);
  const pmf = k => (fact(n)/(fact(k)*fact(n-k)))*Math.pow(p,k)*Math.pow(1-p,n-k);
  const pmfArr = Array.from({length:n+1},(_,k)=>pmf(k));
  const cdfArr = pmfArr.reduce((acc,v,i)=>[...acc,(acc[i-1]||0)+v],[]);

  const dColor='#9b59b6', dFill='#f9f0ff';

  // Continuous CDF: Standard Normal
  const nColor='#2E75B6', nFill='#EBF3FB';
  const normcdf = (x) => 0.5*(1+erf(x/Math.sqrt(2)));
  function erf(x) {
    const a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p2=0.3275911;
    const sign = x<0?-1:1; const t=1/(1+p2*Math.abs(x));
    const y=1-(((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-x*x);
    return sign*y;
  }
  const xMin=-3.5, xMax=3.5;
  const cdfPts = Array.from({length:100},(_,i)=>{ const x=xMin+(xMax-xMin)*i/99; return {x,y:normcdf(x)}; });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Cumulative Distribution Function (CDF) — Discrete and Continuous</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: Discrete CDF (Binomial) */}
        {(() => {
          const px=10, ax=px+52, aw=panW-66, ay=aY;
          const bw=aw/(n+2);
          const toY = (y) => ay+aH-(y)*aH;
          return (
            <g>
              <rect x={px} y={10} width={panW} height={H-16} rx={5} fill={dFill} stroke={dColor} strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={30} rx={3} fill={dColor}/>
              <text x={px+panW/2} y={26} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
                Discrete CDF — Binomial(n=5, p=0.5)
              </text>
              <text x={px+panW/2} y={40} textAnchor="middle" fontSize="10" fill="#ffffffcc">F(k) = P(X ≤ k) — step function</text>

              {/* Grid */}
              {[0,0.2,0.4,0.6,0.8,1.0].map(v=>(
                <g key={v}>
                  <line x1={ax} y1={toY(v)} x2={ax+aw} y2={toY(v)} stroke="#ddd" strokeWidth="0.8"/>
                  <text x={ax-4} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v.toFixed(1)}</text>
                </g>
              ))}
              <line x1={ax} y1={ay} x2={ax} y2={ay+aH} stroke="#bbb" strokeWidth="1"/>
              <line x1={ax} y1={ay+aH} x2={ax+aw} y2={ay+aH} stroke="#bbb" strokeWidth="1"/>

              {/* Step function lines */}
              {cdfArr.map((cdf,k)=>{
                const x1 = ax + (k+0.5)*bw;
                const x2 = ax + (k+1.5)*bw;
                const y = toY(cdf);
                const prevY = k>0 ? toY(cdfArr[k-1]) : ay+aH;
                return (
                  <g key={k}>
                    {/* vertical jump */}
                    <line x1={x1} y1={prevY} x2={x1} y2={y} stroke={dColor} strokeWidth="1.5" strokeDasharray="3,2"/>
                    {/* horizontal step */}
                    {k<=n && <line x1={x1} y1={y} x2={k===n?ax+aw:x2} y2={y} stroke={dColor} strokeWidth="2.5"/>}
                    {/* filled circle at step start */}
                    <circle cx={x1} cy={y} r={4} fill={dColor}/>
                    {/* open circle at end */}
                    {k<n && <circle cx={x2} cy={y} r={3.5} fill="#fff" stroke={dColor} strokeWidth="1.5"/>}
                    {/* x label */}
                    <text x={x1} y={ay+aH+14} textAnchor="middle" fontSize="10" fill="#555">{k}</text>
                    {/* PMF bar hint */}
                    <rect x={x1-bw*0.35} y={ay+aH+22} width={bw*0.7} height={pmfArr[k]*40}
                      fill={dColor} opacity="0.3"/>
                  </g>
                );
              })}
              <text x={ax+aw/2} y={ay+aH+50} textAnchor="middle" fontSize="10" fill="#555">k</text>
              <text x={ax-38} y={ay+aH/2} textAnchor="middle" fontSize="10" fill="#555"
                transform={`rotate(-90,${ax-38},${ay+aH/2})`}>F(k) = P(X≤k)</text>

              <rect x={px+6} y={ay+aH+60} width={panW-12} height={20} rx={3} fill="#fff" stroke={dColor} strokeWidth="1"/>
              <text x={px+panW/2} y={ay+aH+73} textAnchor="middle" fontSize="9.5"
                fill={dColor} fontFamily="'Courier New',monospace" fontWeight="700">F(k) = Σⱼ₌₀ᵏ P(X=j)  — non-decreasing step fn</text>
            </g>
          );
        })()}

        {/* RIGHT: Continuous CDF (Normal) */}
        {(() => {
          const px=10+panW+4, ax=px+52, aw=panW-66, ay=aY;
          const toX = x => ax+(x-xMin)/(xMax-xMin)*aw;
          const toY = y => ay+aH-y*aH;
          const polyPts = cdfPts.map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`).join(' ');
          const p75x = toX(normcdf.__inverse||0.67); // not used directly
          // Mark median (x=0) and quartiles
          const marks = [{x:0,lbl:'50%',col:'#2E75B6'},{x:-0.674,lbl:'25%',col:'#e07b00'},{x:0.674,lbl:'75%',col:'#27ae60'}];
          return (
            <g>
              <rect x={px} y={10} width={panW} height={H-16} rx={5} fill={nFill} stroke={nColor} strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={30} rx={3} fill={nColor}/>
              <text x={px+panW/2} y={26} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
                Continuous CDF — Standard Normal N(0,1)
              </text>
              <text x={px+panW/2} y={40} textAnchor="middle" fontSize="10" fill="#ffffffcc">F(x) = P(X ≤ x) — smooth S-curve</text>

              {/* Grid */}
              {[0,0.25,0.5,0.75,1].map(v=>(
                <g key={v}>
                  <line x1={ax} y1={toY(v)} x2={ax+aw} y2={toY(v)} stroke="#ddd" strokeWidth="0.8"/>
                  <text x={ax-4} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v.toFixed(2)}</text>
                </g>
              ))}
              <line x1={ax} y1={ay} x2={ax} y2={ay+aH} stroke="#bbb" strokeWidth="1"/>
              <line x1={ax} y1={ay+aH} x2={ax+aw} y2={ay+aH} stroke="#bbb" strokeWidth="1"/>

              {/* S-curve */}
              <polyline points={polyPts} fill="none" stroke={nColor} strokeWidth="2.8"/>

              {/* Quartile markers */}
              {marks.map(({x,lbl,col})=>{
                const cx=toX(x), cy=toY(normcdf(x));
                return (
                  <g key={lbl}>
                    <line x1={cx} y1={ay} x2={cx} y2={cy} stroke={col} strokeWidth="1.2" strokeDasharray="4,3"/>
                    <line x1={ax} y1={cy} x2={cx} y2={cy} stroke={col} strokeWidth="1.2" strokeDasharray="4,3"/>
                    <circle cx={cx} cy={cy} r={5} fill={col}/>
                    <text x={cx+6} y={cy-4} fontSize="9" fill={col} fontWeight="700">{lbl}</text>
                  </g>
                );
              })}

              {/* x ticks */}
              {[-3,-2,-1,0,1,2,3].map(v=>(
                <g key={v}>
                  <line x1={toX(v)} y1={ay+aH} x2={toX(v)} y2={ay+aH+4} stroke="#aaa" strokeWidth="1"/>
                  <text x={toX(v)} y={ay+aH+14} textAnchor="middle" fontSize="9" fill="#666">{v}</text>
                </g>
              ))}
              <text x={ax+aw/2} y={ay+aH+27} textAnchor="middle" fontSize="10" fill="#555">x</text>
              <text x={ax-38} y={ay+aH/2} textAnchor="middle" fontSize="10" fill="#555"
                transform={`rotate(-90,${ax-38},${ay+aH/2})`}>F(x) = P(X≤x)</text>

              <rect x={px+6} y={ay+aH+60} width={panW-12} height={20} rx={3} fill="#fff" stroke={nColor} strokeWidth="1"/>
              <text x={px+panW/2} y={ay+aH+73} textAnchor="middle" fontSize="9.5"
                fill={nColor} fontFamily="'Courier New',monospace" fontWeight="700">F(x) = ∫₋∞ˣ f(t)dt — right-continuous, F(−∞)=0, F(+∞)=1</text>
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>
        The CDF F(x) = P(X ≤ x) is a non-decreasing function from 0 to 1. For discrete distributions it is a step function that jumps by exactly P(X=k) at each integer k; for continuous distributions it is a smooth curve whose slope at each point equals the PDF. Any probability P(a {'<'} X ≤ b) = F(b) − F(a) for both types.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Expectation, Variance, and Moments
// ─────────────────────────────────────────────────────────────────
function DiagramMoments() {
  const W = 900, H = 340;
  const panW = (W-28)/2;

  // Left: show E[X] visually as balance point on discrete distribution
  // Binomial n=8, p=0.35
  const n=8, p=0.35;
  const fact = k => k<=1?1:k*fact(k-1);
  const pmf = k => (fact(n)/(fact(k)*fact(n-k)))*Math.pow(p,k)*Math.pow(1-p,n-k);
  const vals = Array.from({length:n+1},(_,k)=>({k,prob:pmf(k)}));
  const mean = n*p; // 2.8
  const variance = n*p*(1-p); // 1.82
  const std = Math.sqrt(variance);
  const maxP = Math.max(...vals.map(v=>v.prob));

  // Right: show variance visually — narrow vs wide distribution
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Expectation and Variance — Mean as Balance Point, Variance as Spread</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: PMF with mean and ±σ marked */}
        {(() => {
          const px=10, ax=px+50, aw=panW-68, ay=40, ah=185;
          const bw = aw/(n+1)-2;
          const toBarX = k => ax + k*(aw/(n+1));
          const toY = prob => ay+ah-(prob/maxP)*ah;
          const meanX = ax + mean*(aw/(n+1)) + bw/2;
          const sigX1 = ax + (mean-std)*(aw/(n+1)) + bw/2;
          const sigX2 = ax + (mean+std)*(aw/(n+1)) + bw/2;

          return (
            <g>
              <rect x={px} y={10} width={panW} height={H-16} rx={5} fill="#fff" stroke="#2E75B6" strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={22} rx={3} fill="#2E75B6"/>
              <text x={px+panW/2} y={30} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
                Expectation E[X] — Binomial(n=8, p=0.35)
              </text>

              {/* Gridlines */}
              {[0.1,0.2,0.3].map(v=>(
                v<=maxP+0.02 ? (
                  <g key={v}>
                    <line x1={ax} y1={toY(v)} x2={ax+aw} y2={toY(v)} stroke="#eee" strokeWidth="0.8"/>
                    <text x={ax-4} y={toY(v)+4} textAnchor="end" fontSize="8" fill="#aaa">{v.toFixed(1)}</text>
                  </g>
                ):null
              ))}
              <line x1={ax} y1={ay} x2={ax} y2={ay+ah} stroke="#ccc" strokeWidth="1"/>
              <line x1={ax} y1={ay+ah} x2={ax+aw} y2={ay+ah} stroke="#ccc" strokeWidth="1"/>

              {/* Bars */}
              {vals.map(({k,prob})=>{
                const bx=toBarX(k); const bh=(prob/maxP)*ah; const by=ay+ah-bh;
                const inSig = Math.abs(k-mean)<=std;
                return (
                  <g key={k}>
                    <rect x={bx+1} y={by} width={bw} height={bh} rx={2}
                      fill={inSig?'#2E75B688':'#2E75B644'} stroke="#2E75B6" strokeWidth={inSig?1.5:0.8}/>
                    <text x={bx+bw/2+1} y={ay+ah+13} textAnchor="middle" fontSize="9" fill="#666">{k}</text>
                  </g>
                );
              })}

              {/* Mean line */}
              <line x1={meanX} y1={ay-4} x2={meanX} y2={ay+ah+2} stroke="#c0392b" strokeWidth="2.5"/>
              <text x={meanX+4} y={ay+10} fontSize="10" fill="#c0392b" fontWeight="700">E[X]=np={mean.toFixed(1)}</text>

              {/* ±σ lines */}
              {[sigX1, sigX2].map((sx, i) => (
                <g key={i}>
                  <line x1={sx} y1={ay} x2={sx} y2={ay+ah} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="5,3"/>
                  <text x={sx} y={ay-5} textAnchor="middle" fontSize="9" fill="#e07b00">{i===0?'−σ':'+σ'}</text>
                </g>
              ))}

              {/* Balance beam */}
              <line x1={ax} y1={ay+ah+26} x2={ax+aw} y2={ay+ah+26} stroke="#888" strokeWidth="2"/>
              <polygon points={`${meanX},${ay+ah+26} ${meanX-7},${ay+ah+38} ${meanX+7},${ay+ah+38}`} fill="#c0392b"/>
              <text x={ax+aw/2} y={ay+ah+50} textAnchor="middle" fontSize="10" fill="#555">
                σ = √(Var)  =  √(np(1-p))  = {std.toFixed(2)}   |   Shaded = within 1σ of mean
              </text>

              <rect x={px+6} y={ay+ah+58} width={panW-12} height={20} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
              <text x={px+panW/2} y={ay+ah+71} textAnchor="middle" fontSize="9.5"
                fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
                E[X]=Σk·P(X=k)   Var(X)=Σ(k−μ)²·P(X=k)=E[X²]−(E[X])²
              </text>
            </g>
          );
        })()}

        {/* RIGHT: Moment summary table */}
        {(() => {
          const px=10+panW+4;
          const items = [
            { label:'E[X] = μ', desc:'First moment. Weighted average of all values.', color:'#c0392b', formula:'Σ xᵢP(xᵢ) or ∫xf(x)dx' },
            { label:'Var(X) = σ²', desc:'Second central moment. Average squared deviation from μ.', color:'#2E75B6', formula:'E[(X−μ)²] = E[X²] − (E[X])²' },
            { label:'Std dev σ', desc:'Square root of variance; same units as X.', color:'#27ae60', formula:'σ = √Var(X)' },
            { label:'Skewness', desc:'Third standardized central moment. Asymmetry of distribution.', color:'#e07b00', formula:'E[(X−μ)³]/σ³   (0 = symmetric)' },
            { label:'Kurtosis', desc:'Fourth standardized central moment. Tail heaviness.', color:'#9b59b6', formula:'E[(X−μ)⁴]/σ⁴   (Normal ≈ 3)' },
            { label:'MGF M(t)', desc:'Moment generating function — encodes all moments.', color:'#1F3864', formula:"M(t) = E[e^(tX)] → μₙ = M^(n)(0)" },
          ];

          return (
            <g>
              <rect x={px} y={10} width={panW} height={H-16} rx={5} fill="#fff" stroke="#1F3864" strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={22} rx={3} fill="#1F3864"/>
              <text x={px+panW/2} y={30} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
                Moments of a Distribution
              </text>
              {items.map(({ label, desc, color, formula }, i) => {
                const iy = 48 + i*46;
                return (
                  <g key={i}>
                    <rect x={px+8} y={iy} width={panW-16} height={40} rx={4} fill={`${color}11`} stroke={color} strokeWidth="1.2"/>
                    <text x={px+18} y={iy+14} fontSize="11" fill={color} fontWeight="700">{label}</text>
                    <text x={px+18} y={iy+27} fontSize="9.5" fill="#555">{desc}</text>
                    <text x={px+18} y={iy+38} fontSize="9" fill={color} fontFamily="'Courier New',monospace">{formula}</text>
                  </g>
                );
              })}
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>
        Expectation E[X] is the probability-weighted average — the balance point of the distribution. Variance Var(X) measures average squared distance from the mean; its square root σ is the standard deviation in original units. Higher moments capture shape: skewness measures asymmetry and kurtosis measures the heaviness of the tails relative to the Normal distribution.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Distribution Relationships and Summary
// ─────────────────────────────────────────────────────────────────
function DiagramRelationships() {
  const W = 900, H = 370;

  // Node positions
  const nodes = [
    { id:'bernoulli', x:120, y:60,  w:160, h:44, color:'#2E75B6', fill:'#EBF3FB',  label:'Bernoulli(p)',     sub:'Single trial' },
    { id:'binomial',  x:120, y:160, w:160, h:44, color:'#2E75B6', fill:'#EBF3FB',  label:'Binomial(n,p)',    sub:'n Bernoullis' },
    { id:'geometric', x:120, y:260, w:160, h:44, color:'#9b59b6', fill:'#f5d0ff',  label:'Geometric(p)',     sub:'Trials to success' },
    { id:'poisson',   x:350, y:60,  w:160, h:44, color:'#e07b00', fill:'#fff7ee',  label:'Poisson(λ)',       sub:'Rare events' },
    { id:'normal',    x:350, y:160, w:160, h:44, color:'#27ae60', fill:'#f0fff4',  label:'Normal(μ,σ²)',     sub:'Bell curve' },
    { id:'uniform',   x:350, y:260, w:160, h:44, color:'#e07b00', fill:'#fff7ee',  label:'Uniform(a,b)',     sub:'Constant density' },
    { id:'expo',      x:580, y:60,  w:160, h:44, color:'#27ae60', fill:'#f0fff4',  label:'Exponential(λ)',   sub:'Memoryless waits' },
    { id:'beta',      x:580, y:160, w:160, h:44, color:'#c0392b', fill:'#fff0f0',  label:'Beta(α,β)',        sub:'Probability models' },
    { id:'gamma',     x:580, y:260, w:160, h:44, color:'#c0392b', fill:'#fff0f0',  label:'Gamma(k,θ)',       sub:'Sum of exponentials' },
    { id:'chisq',     x:760, y:160, w:130, h:44, color:'#1F3864', fill:'#e8eef8',  label:'Chi-squared(k)',   sub:'Sum of N(0,1)²' },
  ];

  // Arrows: [from_id, to_id, label]
  const arrows = [
    ['bernoulli','binomial','n copies → Bin(n,p)'],
    ['binomial','normal','n→∞, CLT'],
    ['poisson','normal','λ→∞, CLT'],
    ['geometric','expo','Continuous limit'],
    ['expo','gamma','Sum of k Expos'],
    ['normal','chisq','X²~N(0,1)²'],
    ['binomial','poisson','n→∞, p→0\nnp=λ'],
  ];

  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  const midX = nd => nd.x + nd.w/2;
  const midY = nd => nd.y + nd.h/2;
  const botY = nd => nd.y + nd.h;
  const topY = nd => nd.y;
  const rightX = nd => nd.x + nd.w;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Distribution Relationships — How Common Distributions Connect</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <marker id="arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#888"/>
          </marker>
        </defs>

        {/* Background */}
        <rect x={5} y={5} width={W-10} height={H-10} rx={6} fill="#f8fbff" stroke="#ccd9ea"/>

        {/* Arrows */}
        {arrows.map(([from,to,lbl],i)=>{
          const fn=nodeMap[from], tn=nodeMap[to];
          if(!fn||!tn) return null;
          // Simple straight line with midpoint label
          const x1=midX(fn), y1=midY(fn), x2=midX(tn), y2=midY(tn);
          const mx=(x1+x2)/2, my=(y1+y2)/2;
          // Shorten ends slightly
          const dx=x2-x1, dy=y2-y1, len=Math.sqrt(dx*dx+dy*dy);
          const sx1=x1+(dx/len)*22, sy1=y1+(dy/len)*22;
          const sx2=x2-(dx/len)*22, sy2=y2-(dy/len)*22;
          return (
            <g key={i}>
              <line x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke="#bbb" strokeWidth="1.5" markerEnd="url(#arr)"/>
              {lbl.split('\n').map((line,j)=>(
                <text key={j} x={mx} y={my-4+j*12} textAnchor="middle" fontSize="9" fill="#888">{line}</text>
              ))}
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map(nd=>(
          <g key={nd.id}>
            <rect x={nd.x} y={nd.y} width={nd.w} height={nd.h} rx={6}
              fill={nd.fill} stroke={nd.color} strokeWidth="2"/>
            <rect x={nd.x+4} y={nd.y+4} width={nd.w-8} height={18} rx={3} fill={nd.color}/>
            <text x={nd.x+nd.w/2} y={nd.y+16} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">
              {nd.label}
            </text>
            <text x={nd.x+nd.w/2} y={nd.y+34} textAnchor="middle" fontSize="9.5" fill={nd.color}>
              {nd.sub}
            </text>
          </g>
        ))}

        {/* Legend: discrete vs continuous */}
        <rect x={10} y={H-44} width={200} height={36} rx={4} fill="#fff" stroke="#ccd9ea"/>
        <text x={20} y={H-27} fontSize="10" fill="#2E75B6" fontWeight="700">■ Discrete distributions</text>
        <text x={20} y={H-13} fontSize="10" fill="#27ae60" fontWeight="700">■ Continuous distributions</text>

        {/* CLT banner */}
        <rect x={220} y={H-44} width={460} height={36} rx={4} fill="#EBF3FB" stroke="#2E75B6"/>
        <text x={450} y={H-27} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">
          Central Limit Theorem: sum of many i.i.d. variables → Normal distribution
        </text>
        <text x={450} y={H-13} textAnchor="middle" fontSize="9.5" fill="#555">
          regardless of the original distribution, as n → ∞
        </text>
      </svg>
      <div style={S.dCaption}>
        Probability distributions form a rich interconnected family. Binomial sums Bernoullis; Poisson is the limit of Binomial as n grows and p shrinks (np=λ fixed); both converge to the Normal by the Central Limit Theorem. The Exponential is the continuous analogue of the Geometric and generalizes to the Gamma; squaring standard Normals yields the Chi-squared, essential for hypothesis testing.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function ProbabilityDistributions() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Probability Distributions</h1>

      <p style={S.p}>
        A probability distribution describes how probability is spread over the possible values of a random variable. For discrete variables, a probability mass function (PMF) assigns a probability to each value; for continuous variables, a probability density function (PDF) describes the relative likelihood at each point, with area representing probability. The two key summaries are the expected value E[X] — the probability-weighted average — and the variance Var(X) — the average squared deviation from the mean.
      </p>

      <InfoBox
        title="Core Vocabulary of Distributions"
        items={[
          "Random variable X: a variable whose value is determined by a random process; takes values in its support.",
          "PMF (discrete): P(X=k) ≥ 0 for each integer k, and the sum over all k equals exactly 1.",
          "PDF (continuous): f(x) ≥ 0 for all x, and ∫f(x)dx = 1 over the entire real line.",
          "CDF: F(x) = P(X ≤ x) — non-decreasing, right-continuous, F(−∞)=0, F(+∞)=1.",
          "Expected value: E[X] = Σ x·P(X=x) for discrete; ∫x·f(x)dx for continuous.",
          "Variance: Var(X) = E[(X−μ)²] = E[X²] − (E[X])²  — shortcut formula is very useful.",
          "Standard deviation σ = √Var(X) — same units as X, easier to interpret than variance.",
          "i.i.d.: independent and identically distributed — the standard assumption for sums and averages.",
        ]}
      />

      <h2 style={S.h2}>Discrete vs Continuous — PMF and PDF</h2>
      <DiagramPMFvsPDF />
      <Table2 rows={[
        ["PMF",              "P(X=k) ≥ 0 and Σₖ P(X=k) = 1\nProbability at each individual point is well-defined"],
        ["PDF",              "f(x) ≥ 0 and ∫₋∞^∞ f(x)dx = 1\nP(X=x) = 0 for any single point; only intervals have probability"],
        ["P(a ≤ X ≤ b)",    "Discrete: Σ_{a≤k≤b} P(X=k)\nContinuous: ∫ₐᵇ f(x)dx — area under the PDF curve"],
        ["Support",          "Set of values where P(X=k)>0 or f(x)>0\nBinomial: {0,1,…,n}; Normal: all real numbers"],
        ["Normalization",    "Total probability must always equal 1\nKey check: PMF sums to 1; PDF integrates to 1"],
        ["Mixed distributions","Some distributions have both discrete and continuous parts\nExample: zero-inflated models, empirical CDFs"],
      ]} />

      <h2 style={S.h2}>Common Discrete Distributions</h2>
      <DiagramDiscreteDistributions />
      <Table2 rows={[
        ["Bernoulli(p)",    "P(X=1)=p, P(X=0)=1-p\nE=p, Var=p(1-p). Single coin flip with bias p."],
        ["Binomial(n,p)",   "P(X=k) = C(n,k)pᵏ(1-p)ⁿ⁻ᵏ, k=0,…,n\nE=np, Var=np(1-p). Count successes in n trials."],
        ["Geometric(p)",    "P(X=k) = (1-p)^(k-1)·p, k=1,2,…\nE=1/p, Var=(1-p)/p². Memoryless: P(X>s+t|X>s)=P(X>t)."],
        ["Poisson(λ)",      "P(X=k) = e^(-λ)·λᵏ/k!, k=0,1,2,…\nE=λ, Var=λ. Models rare events; Binomial limit as n→∞, p→0."],
        ["Hypergeometric",  "Drawing without replacement from finite population\nE=nK/N, Var=nK(N-K)(N-n)/[N²(N-1)]. No replacement → dependent."],
        ["Negative Binomial","P(X=k) = C(k-1,r-1)pʳ(1-p)^(k-r), k=r,r+1,…\nE=r/p, Var=r(1-p)/p². Trials until rth success."],
      ]} />

      <h2 style={S.h2}>Common Continuous Distributions</h2>
      <DiagramContinuousDistributions />
      <Table2 rows={[
        ["Uniform(a,b)",    "f(x) = 1/(b-a) for x∈[a,b], 0 elsewhere\nE=(a+b)/2, Var=(b-a)²/12. All values equally likely."],
        ["Exponential(λ)",  "f(x) = λe^(-λx) for x≥0\nE=1/λ, Var=1/λ². Memoryless; models time between Poisson events."],
        ["Normal(μ,σ²)",    "f(x) = (1/σ√2π)·exp(-(x-μ)²/2σ²)\nE=μ, Var=σ². 68-95-99.7 rule. CLT limit of sums."],
        ["Gamma(k,θ)",      "f(x) = x^(k-1)·e^(-x/θ)/(Γ(k)·θᵏ) for x>0\nE=kθ, Var=kθ². Sum of k independent Exponential(1/θ)."],
        ["Beta(α,β)",       "f(x) = x^(α-1)(1-x)^(β-1)/B(α,β) for x∈[0,1]\nE=α/(α+β). Natural model for probabilities and proportions."],
        ["Log-Normal(μ,σ)", "If Y~N(μ,σ²) then X=eʸ is log-normal\nE=exp(μ+σ²/2). Models multiplicative processes and incomes."],
      ]} />

      <h2 style={S.h2}>Cumulative Distribution Function (CDF)</h2>
      <DiagramCDF />
      <Table2 rows={[
        ["CDF Definition",  "F(x) = P(X ≤ x)\nNon-decreasing, right-continuous, F(−∞)=0, F(+∞)=1"],
        ["Interval probability","P(a < X ≤ b) = F(b) − F(a)\nWorks for both discrete and continuous distributions"],
        ["Quantile / Percentile","Q(p) = F⁻¹(p) = smallest x with F(x)≥p\nMedian = Q(0.5); IQR = Q(0.75)−Q(0.25)"],
        ["Discrete CDF",    "Jumps by P(X=k) at each k; flat between integers\nStep function; right-continuous"],
        ["Continuous CDF",  "Smooth S-shaped curve; slope at x equals f(x)\nF'(x) = f(x) — differentiating the CDF gives the PDF"],
        ["Survival function","S(x) = 1−F(x) = P(X > x)\nCommon in reliability and survival analysis"],
      ]} />

      <h2 style={S.h2}>Expectation, Variance, and Moments</h2>
      <DiagramMoments />
      <Table2 rows={[
        ["Linearity of E",  "E[aX+b] = aE[X]+b; E[X+Y] = E[X]+E[Y] always\nLinearity holds regardless of dependence"],
        ["Variance rules",  "Var(aX+b) = a²Var(X)\nVar(X+Y) = Var(X)+Var(Y)+2Cov(X,Y); simplifies if independent"],
        ["Covariance",      "Cov(X,Y) = E[(X−μₓ)(Y−μᵧ)] = E[XY]−E[X]E[Y]\nCorrelation ρ = Cov(X,Y)/(σₓσᵧ) ∈ [−1,1]"],
        ["Jensen's inequality","If g is convex: E[g(X)] ≥ g(E[X])\nE[X²] ≥ (E[X])² — basis of Var(X) ≥ 0"],
        ["Chebyshev's inequality","P(|X−μ|≥kσ) ≤ 1/k²  for any distribution\nAt least 75% of values within 2σ; 89% within 3σ"],
        ["MGF M(t)=E[e^tX]","d^n/dt^n M(t)|_{t=0} = E[Xⁿ] — nth moment\nUniquely determines the distribution when it exists"],
      ]} />

      <h2 style={S.h2}>Distribution Relationships and the Central Limit Theorem</h2>
      <DiagramRelationships />
      <Table2 rows={[
        ["Central Limit Theorem","X̄ₙ = (X₁+…+Xₙ)/n → N(μ, σ²/n) as n→∞\nHolds for any i.i.d. distribution with finite μ and σ²"],
        ["Law of Large Numbers","X̄ₙ → μ as n→∞ (both weak and strong LLN)\nSample mean converges to population mean"],
        ["Bin→Poisson limit",   "Binomial(n,p) → Poisson(λ) when n→∞, p→0, np=λ\nUseful when n is large and p is very small"],
        ["Bin→Normal limit",    "Binomial(n,p) → N(np, np(1-p)) when n large\nNormal approximation: use when np≥5 and n(1-p)≥5"],
        ["Sum of Normals",       "If Xᵢ~N(μᵢ,σᵢ²) independent: ΣXᵢ~N(Σμᵢ, Σσᵢ²)\nSum of independent Normals is always Normal"],
        ["Chi-squared",         "If Zᵢ~N(0,1) i.i.d.: Z₁²+…+Zₖ²~χ²(k)\nFoundation of t-tests, ANOVA, and goodness-of-fit tests"],
      ]} />

      <InfoBox
        title="Key Takeaway — Choosing the Right Distribution"
        items={[
          "Binary outcome (success/fail), single trial? → Bernoulli(p)",
          "Count of successes in n independent trials? → Binomial(n, p)",
          "Count of trials until first success? → Geometric(p)",
          "Count of rare events in a fixed time or space? → Poisson(λ)",
          "Continuous waiting time between events? → Exponential(λ)  [memoryless]",
          "Completely random value in an interval, no preference? → Uniform(a, b)",
          "Symmetric bell curve, sum of many effects, CLT context? → Normal(μ, σ²)",
          "Modeling a probability or proportion as a random variable? → Beta(α, β)",
          "Sum of k independent Exponentials, total service times? → Gamma(k, θ)",
          "Always verify: does your PMF sum to 1 or does your PDF integrate to 1?",
        ]}
      />
    </div>
  );
}